// src/pages/PagesJudokas/PricingPage/PricingPage.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../../Home/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowRight,
  FaBolt,
  FaCheck,
  FaCheckCircle,
  FaCrown,
  FaHeart,
  FaLayerGroup,
  FaMedal,
  FaShieldAlt,
  FaStar,
  FaSyncAlt,
  FaUser,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import { IoSparklesSharp } from "react-icons/io5";

import { getRoleFromRoleId } from "../../../utils/roleHelper";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "../../../hooks/useScollToTop";
import { createCheckoutSessionForPlan } from "../../../services/paymentServices";
import { getUserById } from "../../../services/userServices";
import { sendNotificationToAdmin } from "./../../../services/notificationServices";
import DiaporamaJudoCoachPro from "./DiaporamaJudoCoachPro";
import backgroundImage from "./HeaderSlider_02.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function safeParseUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error("Erreur lecture userInfo localStorage :", error);
    localStorage.removeItem("userInfo");
    return null;
  }
}

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      {
        threshold: 0.14,
        rootMargin: "0px 0px -40px 0px",
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-700 ease-out",
        visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
        className,
      )}
      style={{
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const PAID_COACH_FEATURES = [
  "Gestion des groupes de judokas",
  "Création de compétitions et d’événements pour vos élèves",
  "Invitations à participer à des compétitions et suivi des résultats",
  "Suivi des passages de grades et palmarès de vos élèves",
  "Appels de présence et gestion des effectifs",
  "Statistiques avancées sur les élèves et les groupes",
  "Préparation physique pour vos élèves",
];

const PLAN_STYLES = {
  free: {
    accent: "from-emerald-400 to-lime-500",
    glow: "bg-emerald-500/20",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: FaUserGraduate,
  },
  pro: {
    accent: "from-blue-400 to-indigo-600",
    glow: "bg-blue-500/20",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    icon: FaMedal,
  },
  ultim: {
    accent: "from-orange-400 to-rose-600",
    glow: "bg-orange-500/20",
    badge: "bg-orange-50 text-orange-700 border-orange-200",
    icon: FaCrown,
  },
};

const plans = [
  {
    key: "free",
    name: "Judo Expert",
    subtitle: "Découverte",
    priceLabel: "Gratuit",
    priceDetail: "Accès de base",
    annualText: "Pour découvrir l’application",
    role_id: 3,
    product_id: null,
    formule: "Judo Expert",
    highlighted: false,
    features: [
      "Accès à l’espace personnel judoka",
      "Rejoindre un groupe suivi par un coach",
      "Consulter les messages et invitations de coachs sur les compétitions",
      "Suivi des résultats, palmarès, passages de grades et statistiques personnelles",
      "Consultation des techniques disponibles, kata, arbitrage, ...",

    ],
    limits: [
      {
        label: "Groupes",
        value: "0",
        detail: "pas de gestion coach",
        percent: 0,
      },
      {
        label: "Judokas",
        value: "Profil perso",
        detail: "usage individuel",
        percent: 25,
      },
    ],
    bestFor:
      "Pour les judokas, parents ou utilisateurs qui souhaitent découvrir Judo Coach Pro.",
  },
  {
    key: "pro",
    name: "Coach Pro",
    subtitle: "Clubs & coachs",
    priceLabel: "20€/an",
    priceDetail: "Abonnement annuel",
    annualText: "Jusqu’à 3 groupes et moins de 100 adhérents",
    role_id: 6,
    product_id: "prod_RYkqSEZaRI41Ho",
    amount: 20,
    formule: "Coach Pro",
    highlighted: true,
    features: PAID_COACH_FEATURES,
    limits: [
      {
        label: "Groupes",
        value: "3 max",
        detail: "idéal petit club ou section",
        percent: 55,
      },
      {
        label: "Judokas",
        value: "< 100",
        detail: "moins de 100 adhérents",
        percent: 65,
      },
    ],
    bestFor:
      "Pour les enseignants qui suivent quelques groupes et souhaitent structurer leur pédagogie.",
  },
  {
    key: "ultim",
    name: "Coach Ultim",
    subtitle: "Grande structure",
    priceLabel: "30€/an",
    priceDetail: "Abonnement annuel",
    annualText: "Plus de 3 groupes ou plus de 100 adhérents",
    role_id: 7,
    product_id: "prod_RcwAgO7NKbdOJG",
    amount: 30,
    formule: "Coach Ultim",
    highlighted: false,
    features: PAID_COACH_FEATURES,
    limits: [
      {
        label: "Groupes",
        value: "3+",
        detail: "plus de 3 groupes",
        percent: 100,
      },
      {
        label: "Judokas",
        value: "100+",
        detail: "plus de 100 adhérents",
        percent: 100,
      },
    ],
    bestFor:
      "Pour les clubs plus importants, les responsables techniques ou les coachs très actifs.",
  },
];

const stats = [
  {
    label: "Abonnement",
    value: "20€/an",
    detail: "à partir de",
    icon: FaBolt,
  },
  {
    label: "Pensé pour le dojo",
    value: "100%",
    detail: "judo",
    icon: FaMedal,
  },
  {
    label: "Paiement sécurisé",
    value: "Stripe",
    detail: "en ligne",
    icon: FaShieldAlt,
  },
];

const PricingPage = () => {
  useScrollToTop();

  const { user, updateUser } = useUser();
  const { addNotification } = useNotification();

  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [activePlanKey, setActivePlanKey] = useState(null);

  const userInfo = useMemo(() => safeParseUserInfo(), []);
  const userId = userInfo?.userId || userInfo?.id || user?.userId || user?.id;
  const currentRoleId = user?.role_id || userInfo?.role_id || null;
  const isAuthenticated = Boolean(userId);

  const userFullName = useMemo(() => {
    const prenom = userInfo?.prenom || user?.prenom || "";
    const nom = userInfo?.nom || user?.nom || "";

    return `${prenom} ${nom}`.trim() || "Utilisateur";
  }, [userInfo, user]);

  const userPhoto = useMemo(() => {
    return (
      userInfo?.photoURL ||
      userInfo?.photo_url ||
      user?.photoURL ||
      user?.photo_url ||
      "/img/user_avatar.jpg"
    );
  }, [userInfo, user]);

  const currentRoleLabel = useMemo(() => {
    if (!currentRoleId) return "Chargement...";

    if (currentRoleId === 1) return "Administrateur";

    return getRoleFromRoleId(currentRoleId) || "Utilisateur";
  }, [currentRoleId]);

  const currentPlan = useMemo(() => {
    return plans.find((plan) => plan.role_id === currentRoleId);
  }, [currentRoleId]);

  useEffect(() => {
    const storedUserInfo = safeParseUserInfo();

    if (!storedUserInfo || (!storedUserInfo.userId && !storedUserInfo.id)) {
      localStorage.removeItem("userInfo");
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get("success") === "true") {
      addNotification(
        "Transaction réussie ! Cliquez sur “Rafraîchir la formule” pour mettre à jour votre compte.",
        "success",
      );
      navigate("/formules-tarifs", { replace: true });
    }

    if (searchParams.get("canceled") === "true") {
      addNotification("Transaction annulée.", "warning");
      navigate("/formules-tarifs", { replace: true });
    }
  }, [location, addNotification, navigate]);

  const getPlanState = (plan) => {
    if (!isAuthenticated) return "login_required";
    if (currentRoleId === 1) return "admin";
    if (currentRoleId === plan.role_id) return "current";
    if (currentRoleId && currentRoleId > plan.role_id) {
      return "downgrade_disabled";
    }
    if (!plan.product_id) return "free";
    return "available";
  };

  const handlePurchase = async (plan) => {
    const state = getPlanState(plan);

    if (state === "login_required") {
      if (!plan.product_id) {
        navigate("/register");
        return;
      }

      addNotification(
        "Veuillez vous connecter ou créer un compte avant de choisir une formule.",
        "warning",
      );
      navigate("/login");
      return;
    }

    if (state === "admin") {
      addNotification(
        "Un administrateur n’a pas besoin de souscrire.",
        "warning",
      );
      return;
    }

    if (state === "current") {
      addNotification("Vous utilisez déjà cette formule.", "warning");
      return;
    }

    if (state === "downgrade_disabled") {
      addNotification(
        "Cette formule n’est pas disponible car vous possédez déjà une formule supérieure.",
        "warning",
      );
      return;
    }

    if (!plan.product_id) {
      addNotification(
        "Cette formule gratuite est disponible automatiquement à la création du compte.",
        "success",
      );
      return;
    }

    if (!userId) {
      addNotification(
        "Impossible de récupérer votre ID utilisateur. Veuillez vous reconnecter.",
        "error",
      );
      return;
    }

    if (!plan.formule) {
      console.error("Plan incomplet :", plan);
      addNotification("Formule invalide. Veuillez réessayer.", "error");
      return;
    }

    try {
      setLoading(true);
      setActivePlanKey(plan.key);

      const checkoutUrl = await createCheckoutSessionForPlan(
        {
          formule: plan.formule,
        },
        userId,
      );

      const token = localStorage.getItem("token");
      const message = `L'utilisateur ${userFullName} a tenté de souscrire à la formule ${plan.formule} pour ${plan.priceLabel}.`;

      try {
        await sendNotificationToAdmin(message, token);
      } catch (notificationError) {
        console.warn("Notification admin non envoyée :", notificationError);
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Erreur lors de la création de la session :", error);

      addNotification(
        "Erreur lors de la création de la session de paiement. Veuillez réessayer.",
        "error",
      );
    } finally {
      setLoading(false);
      setActivePlanKey(null);
    }
  };

  const handleRefresh = async () => {
    if (!userId) {
      addNotification(
        "Impossible de rafraîchir les données. Veuillez vous reconnecter.",
        "error",
      );
      return;
    }

    try {
      setLoading(true);

      const updatedUser = await getUserById(userId);

      if (updatedUser) {
        updateUser(updatedUser);

        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            ...userInfo,
            ...updatedUser,
            userId: updatedUser.userId || updatedUser.id || userId,
          }),
        );

        addNotification(
          "Votre formule a été mise à jour avec succès.",
          "success",
        );
      } else {
        addNotification("Utilisateur non trouvé.", "error");
      }
    } catch (error) {
      console.error("Erreur lors du rafraîchissement :", error);

      addNotification(
        "Erreur lors du rafraîchissement des données. Veuillez réessayer.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderPlanButton = (plan) => {
    const state = getPlanState(plan);
    const isPlanLoading = loading && activePlanKey === plan.key;

    if (state === "login_required") {
      return (
        <button
          type="button"
          onClick={() => handlePurchase(plan)}
          className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-slate-950 px-5 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
        >
          {plan.product_id
            ? "Se connecter pour souscrire"
            : "Créer un compte gratuit"}
          <FaArrowRight className="text-xs" />
        </button>
      );
    }

    if (state === "admin") {
      return (
        <button
          type="button"
          disabled
          className="inline-flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-full bg-slate-200 px-5 py-3.5 text-sm font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400"
        >
          Accès administrateur
        </button>
      );
    }

    if (state === "current") {
      return (
        <button
          type="button"
          disabled
          className="inline-flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-full bg-emerald-500 px-5 py-3.5 text-sm font-bold text-white shadow-lg"
        >
          <FaCheckCircle />
          Formule actuelle
        </button>
      );
    }

    if (state === "downgrade_disabled") {
      return (
        <button
          type="button"
          disabled
          className="inline-flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-full bg-slate-200 px-5 py-3.5 text-sm font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400"
        >
          Non disponible
        </button>
      );
    }

    if (state === "free") {
      return (
        <button
          type="button"
          onClick={() => handlePurchase(plan)}
          className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3.5 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          Formule gratuite
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={() => handlePurchase(plan)}
        disabled={loading}
        className={cn(
          "inline-flex w-full items-center justify-center gap-3 rounded-full px-5 py-3.5 text-sm font-bold text-white shadow-lg transition",
          "bg-primary hover:-translate-y-0.5 hover:brightness-110",
          "focus:outline-none focus:ring-4 focus:ring-primary/20",
          "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:brightness-100",
        )}
      >
        {isPlanLoading ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Redirection...
          </>
        ) : (
          <>
            Souscrire
            <FaArrowRight className="text-xs" />
          </>
        )}
      </button>
    );
  };

  return (
    <main className="min-h-screen bg-slate-50 pt-[92px] font-satoshi text-slate-950 dark:bg-slate-950 dark:text-white">
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url("${backgroundImage}")`,
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/82 to-slate-950/50" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

        <div className="absolute left-[-140px] top-20 h-96 w-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute bottom-[-180px] right-[-120px] h-[420px] w-[420px] rounded-full bg-blue-500/20 blur-3xl" />

        <div className="relative mx-auto grid max-w-[1280px] gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-16">
          <Reveal className="flex flex-col justify-center">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/75 backdrop-blur-xl">
              <IoSparklesSharp className="text-primary" />
              Formules Judo Coach Pro
            </div>

            <h1 className="mt-7 max-w-3xl font-barlow text-4xl font-bold leading-tight text-white drop-shadow-2xl sm:text-5xl lg:text-6xl">
              Choisissez la formule adaptée à votre pratique du judo.
            </h1>

            <div className="mt-5 max-w-2xl rounded-[1.75rem] border border-white/10 bg-slate-950/45 p-5 shadow-2xl backdrop-blur-md">
              <p className="text-base font-medium leading-8 text-white/80 sm:text-lg">
                Des formules simples, annuelles et pensées pour les judokas,
                enseignants et coachs. La formule payante démarre à{" "}
                <span className="font-bold text-white">20€/an</span> avec Coach
                Pro.
              </p>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {stats.map((item, index) => {
                const Icon = item.icon;

                return (
                  <Reveal key={item.label} delay={index * 90}>
                    <div className="rounded-[1.7rem] border border-white/10 bg-white/10 p-4 backdrop-blur-xl">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-primary">
                          <Icon />
                        </div>

                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/50">
                            {item.label}
                          </p>
                          <p className="mt-1 font-barlow text-2xl font-bold text-white">
                            {item.value}
                          </p>
                          <p className="text-xs font-semibold text-white/60">
                            {item.detail}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#formules"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-primary px-6 py-4 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
              >
                Voir les formules
                <FaArrowRight className="text-xs" />
              </a>

              {!isAuthenticated && (
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/10 px-6 py-4 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950"
                >
                  Créer un compte
                </button>
              )}
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="relative rounded-[2.5rem] border border-white/10 bg-white/10 p-5 shadow-2xl backdrop-blur-2xl">
              <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary/30 blur-3xl" />

              {isAuthenticated ? (
                <div className="relative overflow-hidden rounded-[2rem] bg-white p-5 text-slate-950 shadow-xl dark:bg-slate-900 dark:text-white">
                  <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary via-orange-400 to-blue-500" />

                  <div className="flex items-center gap-4">
                    <img
                      src={userPhoto}
                      alt={userFullName}
                      className="h-16 w-16 rounded-2xl border border-slate-200 object-cover shadow-md dark:border-white/10"
                      onError={(event) => {
                        event.currentTarget.src = "/img/user_avatar.jpg";
                      }}
                    />

                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                        Votre espace
                      </p>

                      <h2 className="mt-1 truncate font-barlow text-2xl font-bold">
                        {userFullName}
                      </h2>

                      <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                        Formule actuelle :{" "}
                        <span className="text-primary">
                          {currentPlan?.name || currentRoleLabel}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                    <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Après une transaction réussie, vous pouvez rafraîchir vos
                      informations pour afficher votre nouvelle formule.
                    </p>

                    <button
                      type="button"
                      onClick={handleRefresh}
                      disabled={loading}
                      className={cn(
                        "mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full px-5 py-3 text-sm font-bold text-white shadow-lg transition",
                        "bg-slate-950 hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white",
                        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0",
                      )}
                    >
                      {loading && !activePlanKey ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Chargement...
                        </>
                      ) : (
                        <>
                          <FaSyncAlt />
                          Rafraîchir la formule
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative overflow-hidden rounded-[2rem] bg-white p-6 text-slate-950 shadow-xl dark:bg-slate-900 dark:text-white">
                  <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary via-orange-400 to-blue-500" />

                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <FaUser />
                  </div>

                  <h2 className="mt-5 font-barlow text-3xl font-bold">
                    Connectez-vous pour souscrire.
                  </h2>

                  <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                    Vous pouvez consulter les formules librement. Pour activer
                    un abonnement, il faut être connecté à votre compte.
                  </p>

                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
                    >
                      Se connecter
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate("/register")}
                      className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                    >
                      Créer un compte
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section
        id="formules"
        className="relative mx-auto max-w-[1280px] px-4 py-12 sm:px-6 lg:px-8"
      >
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-primary">
            <FaStar />
            Abonnements annuels
          </div>

          <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
            Trois formules simples.
          </h2>

          <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
            Les deux formules payantes donnent accès aux mêmes fonctionnalités
            coach. La différence se fait uniquement sur la jauge de groupes et
            de judokas suivis.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {plans.map((plan, index) => {
            const style = PLAN_STYLES[plan.key];
            const Icon = style.icon;
            const isCurrent = currentRoleId === plan.role_id;

            return (
              <Reveal key={plan.key} delay={index * 100}>
                <article
                  className={cn(
                    "group relative flex h-full flex-col overflow-hidden rounded-[2rem] border bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:bg-slate-900",
                    plan.highlighted
                      ? "border-primary/40 ring-4 ring-primary/10"
                      : "border-slate-200 dark:border-white/10",
                  )}
                >
                  <div
                    className={cn(
                      "absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl transition group-hover:scale-125",
                      style.glow,
                    )}
                  />

                  <div
                    className={cn(
                      "absolute inset-x-0 top-0 h-2 bg-gradient-to-r",
                      style.accent,
                    )}
                  />

                  {plan.highlighted && (
                    <div className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-bold text-white shadow-lg dark:bg-white dark:text-slate-950">
                      <IoSparklesSharp className="text-primary" />
                      Recommandée
                    </div>
                  )}

                  {isCurrent && (
                    <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white shadow-lg">
                      <FaCheckCircle />
                      Actuelle
                    </div>
                  )}

                  <div className="relative pt-8">
                    <div
                      className={cn(
                        "flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg",
                        style.accent,
                      )}
                    >
                      <Icon className="text-xl" />
                    </div>

                    <p
                      className={cn(
                        "mt-5 inline-flex rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.14em]",
                        style.badge,
                      )}
                    >
                      {plan.subtitle}
                    </p>

                    <h3 className="mt-4 font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                      {plan.name}
                    </h3>

                    <div className="mt-5">
                      <p className="font-barlow text-4xl font-bold text-slate-950 dark:text-white">
                        {plan.priceLabel}
                      </p>

                      <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                        {plan.priceDetail}
                      </p>

                      <p className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600 dark:bg-white/5 dark:text-slate-300">
                        {plan.annualText}
                      </p>
                    </div>

                    <div className="mt-5 grid gap-3">
                      {plan.limits.map((limit) => (
                        <div
                          key={`${plan.key}-${limit.label}`}
                          className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-primary shadow-sm dark:bg-slate-950">
                                {limit.label === "Groupes" ? (
                                  <FaLayerGroup />
                                ) : (
                                  <FaUsers />
                                )}
                              </div>

                              <div>
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                                  {limit.label}
                                </p>
                                <p className="font-barlow text-xl font-bold text-slate-950 dark:text-white">
                                  {limit.value}
                                </p>
                              </div>
                            </div>

                            <p className="text-right text-xs font-semibold text-slate-500 dark:text-slate-400">
                              {limit.detail}
                            </p>
                          </div>

                          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                            <div
                              className={cn(
                                "h-full rounded-full bg-gradient-to-r",
                                style.accent,
                              )}
                              style={{ width: `${limit.percent}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <p className="mt-5 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {plan.bestFor}
                    </p>

                    <ul className="mt-6 space-y-3">
                      {plan.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-3 text-sm font-semibold leading-6 text-slate-700 dark:text-slate-200"
                        >
                          <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <FaCheck className="text-[10px]" />
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="relative mt-auto pt-7">
                    {renderPlanButton(plan)}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 pb-12 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-8">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />

            <div className="relative grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-pink-200 bg-pink-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-pink-700 dark:border-pink-500/20 dark:bg-pink-500/10 dark:text-pink-300">
                  <FaHeart />
                  Soutenir le projet
                </div>

                <h3 className="mt-5 font-barlow text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
                  Judo Coach Pro est un projet pensé pour les judokas et les
                  enseignants.
                </h3>

                <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  Les abonnements permettent de maintenir l’application, de
                  développer de nouvelles fonctionnalités et d’améliorer les
                  outils proposés aux clubs, coachs et pratiquants.
                </p>

                <p className="mt-3 max-w-3xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  Vous pouvez également soutenir le projet librement via un don.
                </p>
              </div>

              <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-pink-100 text-pink-600 dark:bg-pink-500/10 dark:text-pink-300">
                    <FaHeart />
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Don libre
                    </p>
                    <p className="mt-1 font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                      Soutenir Judo Coach Pro
                    </p>
                  </div>
                </div>

                <a
                  href="https://buy.stripe.com/fZe8xlaqLgYkdj2fYZ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-pink-600 px-6 py-4 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-pink-700"
                >
                  <FaHeart />
                  Faire un don
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* <section className="pb-12">
        <DiaporamaJudoCoachPro />
      </section> */}
    </main>
  );
};

export default PricingPage;
